import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { resolveAccrualLineItemDefaultAccountIds } from "@common/helpers/accrualAccount";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useHasAccrualLedger } from "@api/ledger.api";
import { appendAccountIds } from "../invoiceLedgerAccountIds";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import { AccrualMatchTableCallbacks, MatchableItem } from "../types";
import { AccountListCell } from "./AccountListCell";
import { RemainingLedgerAccountCell } from "./RemainingLedgerAccountCell";

/**
 * Read-only "Accrual ledger" column for the default (workspace-matched)
 * match drawer view (PRO-12048). Shown alongside `CashLedgerCell` for
 * dual-ledger workspaces. Blank when the item's entity has no accrual
 * ledger; the column itself is only offered in the view when the source
 * transaction's entity has one (see accrualMatchViewConfig).
 */
const ReadOnlyAccrualLedgerCell = memo(
    ({ row }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const {
            accrualAccountIds = [],
            usesDefaultReceivableAccount = false,
            entityId,
            ledgerEntityId,
        } = row.original;
        // Load the chart for the entity that owns the transaction's ledger,
        // which differs from the display `entityId` when a non-business
        // transaction sits in a business financial account (PRO-12048).
        const accountsEntityId = ledgerEntityId ?? entityId;
        const showAccountNumbers = useShowAccountNumbers();
        const hasAccrualLedger = useHasAccrualLedger(
            accountsEntityId || undefined,
        );
        const { accounts } = useAccounts({
            entityId: hasAccrualLedger
                ? accountsEntityId || undefined
                : undefined,
        });
        const accountIds = useMemo(() => {
            const { accrualAccountId } =
                resolveAccrualLineItemDefaultAccountIds({
                    mode: "invoices",
                    accounts,
                });
            return appendAccountIds(
                accrualAccountIds,
                usesDefaultReceivableAccount ? [accrualAccountId] : [],
            );
        }, [accounts, accrualAccountIds, usesDefaultReceivableAccount]);

        return (
            <AccountListCell
                accountIds={accountIds}
                accounts={accounts}
                showAccountNumbers={showAccountNumbers}
            />
        );
    },
);
ReadOnlyAccrualLedgerCell.displayName = "ReadOnlyAccrualLedgerCell";

/**
 * Remaining rows pick the accrual account that absorbs the leftover. That
 * editor lives on this column so the allocation step can show the same
 * Accrual ledger column as the find-match step.
 */
export const AccrualLedgerCell = memo(
    (props: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = useViewTableMeta<
            AccrualMatchTableCallbacks,
            AllocationTableOptions
        >(props.table);
        if (meta.options?.section === "remaining") {
            return (
                <RemainingLedgerAccountCell
                    {...props}
                    ledgerBasis={AccountingLedgerBasis.accruals}
                    accountId={props.row.original.accountIds[0]}
                    onAccountChange={meta.callbacks?.onRemainingAccountChange}
                />
            );
        }
        return <ReadOnlyAccrualLedgerCell {...props} />;
    },
);
AccrualLedgerCell.displayName = "TransactionMatchAccrualLedgerCell";
